import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Hook that checks the `custom:isAdmin` claim from the Cognito ID token payload.
 * Returns whether the current user has admin privileges.
 *
 * Requirements: 1.9, 5.1, 5.2
 */
export function useIsAdmin(): { isAdmin: boolean; isLoading: boolean } {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function checkAdminClaim() {
      try {
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload;

        if (!cancelled) {
          // The custom:isAdmin claim is mapped from the SAML assertion.
          // It may be a string "true" or boolean true depending on Cognito config.
          const adminClaim = payload?.['custom:isAdmin'];
          setIsAdmin(adminClaim === 'true' || adminClaim === true);
        }
      } catch {
        if (!cancelled) {
          setIsAdmin(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    checkAdminClaim();

    return () => {
      cancelled = true;
    };
  }, []);

  return { isAdmin, isLoading };
}
